import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ParkCycleCard } from "@/components/cycle/ParkCycleCard";

function todayDdMmYyyy() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
}

export default function CleaningCyclesOverviewPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/admin/cleaning-cycles/overview")
      .then(({ data }) => { if (!cancel) setRows(Array.isArray(data) ? data : []); })
      .catch((e) => { if (!cancel) { setRows([]); toast.error("Could not load Park Pulse overview"); console.error(e); } })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((p) => (p.park_name || "").toLowerCase().includes(needle) || (p.park_code || "").toLowerCase().includes(needle));
  }, [rows, q]);
  const totals = useMemo(() => {
    const live = rows.filter((p) => (p.assets_count || 0) > 0);
    const overdueParks = live.filter((p) => (p.current_cycle?.pace || "") === "behind");
    const totalAssets = live.reduce((s, p) => s + (p.assets_count || 0), 0);
    const totalOverdue = live.reduce((s, p) => s + (p.overdue_count || 0), 0);
    return { live: live.length, total: rows.length, overdueParks: overdueParks.length, totalAssets, totalOverdue };
  }, [rows]);

  return (
    <AppShell title="Park Pulse Atlas" subtitle={`Cleaning cycle health across all SIPCOT parks · ${todayDdMmYyyy()}`}>
      <div className="space-y-5" data-testid="cleaning-cycles-overview-page">
        {/* Header band */}
        <Card>
          <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><Sparkles className="h-5 w-5" /></div>
              <div>
                <div className="font-display text-lg font-semibold leading-tight">All-Parks Cycle Overview</div>
                <div className="text-sm text-slate-500">Every card is a live pulse — neglected parks float to the top by pace, with the most-missed assets surfaced first inside.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Parks tracked" value={`${totals.live}/${totals.total}`} testid="overview-stat-parks" />
              <Stat label="Assets" value={totals.totalAssets} testid="overview-stat-assets" />
              <Stat label="Behind SLA" value={totals.overdueParks} tone={totals.overdueParks > 0 ? "rose" : "emerald"} testid="overview-stat-behind" />
              <Stat label="Idle assets" value={totals.totalOverdue} tone={totals.totalOverdue > 0 ? "amber" : "emerald"} testid="overview-stat-idle" />
            </div>
          </CardContent>
        </Card>

        {/* Search + legend */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search park by name or code…" className="pl-9" data-testid="park-search-input" />
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600" data-testid="overview-legend">
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Complete</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-500" /> In progress</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-rose-500" /> Overdue</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-300" /> Not started</span>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="park-cycle-grid-loading">
            {Array.from({ length: 8 }).map((_, i) => (<Skeleton key={i} className="h-56 w-full rounded-2xl" />))}
          </div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-sm text-slate-500">No parks match your search.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="park-cycle-grid">
            {filtered.map((p) => (<ParkCycleCard key={p.park_id} park={p} />))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Stat({ label, value, tone = "slate", testid }) {
  const map = { slate: "text-slate-800", emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-700" };
  return (
    <div className="flex flex-col rounded-xl border bg-slate-50/60 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-base font-semibold tabular-nums md:text-lg ${map[tone] || map.slate}`}>{value}</div>
    </div>
  );
}
